import { Address4, Address6 } from 'ip-address';
import jwtDecode from 'jwt-decode';
import * as yup from 'yup';

export const ErrorLengthMin = (min: number) => `Must contain at least ${min} characters`;
export const ErrorLengthMax = (max: number) => `Must contain a maximum of ${max} characters`;
export const ERROR_REQUIRED = 'Required';

// https://github.com/jquense/yup/issues/222
// https://www.regextester.com/93987
// 192.168.0.1/0
// 192.168.0.1/1
// 192.168.0.1/255
// 192.168.0.1
// 192.168.0.1/
// ^([0-9]{1,3}\.){3}[0-9]{1,3}(\/([0-1]?[0-9]?[0-9]?|2[0-4][0-9]|25[0-5]))$
function ipv4WithCIDR(this: any, message: string = 'Invalid IPv4 address - Must follow the pattern IPv4/CIDR(0-32) - Ex: x.x.x.x/[0-32]') {
  // x.x.x.x/[0-255]
  return this.test({
    name: 'ip',
    // eslint-disable-next-line
    test: function (value: string) {
      const { path, createError } = this;
      try {
        const ip = new Address4(value);
        if (ip.parsedSubnet === '') {
          return createError({ path, message: 'IPv4 is valid but a subnet (CIDR) is expected' });
        }
      } catch (e) {
        const error = e as any;
        if (error.message === 'Invalid subnet mask.') {
          return createError({ path, message: 'The subnet (CIDR) should be included between 0 and 32' });
        }
        return createError({ path, message });
      }
      return true;
    },
  });
}

function ipv4(this: any, message: string = 'Invalid IPv4 address - Ex: x.x.x.x') {
  // x.x.x.x/[0-255]
  return this.matches(
    /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
    {
      message,
      excludeEmptyString: true,
    },
  );
}

// fd00:0000:1400:1500:1600::1
// fe80:0000:0000:0000:0204:61ff:fe9d:f156/3
// fe80:0000:0000:0000:0204:61ff:fe9d:f156/254
// fe80:0000:0000:0000:0204:61ff:fe9d:f156
// fe80:0000:0000:0000:0204:61ff:fe9d:f156/a
// fe80:0000:0000:0000:0204:61ff:fe9d:f156/
// fe80:0000:0000:0000:0204:61ff:fe9d:f156/256
function ipv6WithCIDR(this: any, message: string = 'Invalid IPv6 address - Must follow the pattern IPv6/CIDR(0-128) - Ex: fd00:1234:5678:abcd::/64') {
  return this.test({
    name: 'ip',
    // eslint-disable-next-line
    test: function (value: string) {
      const { path, createError } = this;
      try {
        const ip = new Address6(value);
        if (ip.parsedSubnet === '') {
          return createError({ path, message: 'IPv6 is valid but a subnet (CIDR) is expected' });
        }
      } catch (e) {
        const error = e as any;
        if (error.message === 'Invalid subnet mask.') {
          return createError({ path, message: 'The subnet (CIDR) should be included between 0 and 128' });
        }
        return createError({ path, message });
      }
      return true;
    },
  });
}

function ipv6(this: any, message: string = 'Invalid IPv6 address - Ex: fd00:1234:5678:abcd::') {
  return this.matches(
    /(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))/,
    {
      message,
      excludeEmptyString: true,
    },
  );
}

// eslint-disable-next-line no-unused-vars
function ipIsInSubnet(this: any, fieldNameIPType: string, fieldNameIPAddress: string, message: string = 'Not included in the cidr range') {
  return this.test({
    name: 'ip-is-in-subnet',
    // eslint-disable-next-line
    test: function (value: string) {
      const { path, createError, parent } = this;
      if (value !== '') {
        let ip;
        let ipOrigin;
        try {
          switch (parent[fieldNameIPType]) {
            case 'V4':
              ip = new Address4(value);
              ipOrigin = new Address4(parent[fieldNameIPAddress]);
              break;
            case 'V6':
              ip = new Address6(value);
              ipOrigin = new Address6(parent[fieldNameIPAddress]);
              break;
            default:
              break;
          }
        } catch (error) {
          return createError({ path, message: 'Error is not IPv4 or IPv6' });
        }
        try {
          if (ip?.isInSubnet(ipOrigin as any) === false) {
            return createError({ path, message: `IP address must be in '${fieldNameIPAddress}' range` });
          }
        } catch (error) {
          return createError({ path, message: 'Error on isInSubnet check' });
        }
      }
      return true;
    },
  });
}

// eslint-disable-next-line no-unused-vars
function ipIsOver(this: any, fieldNameIPType: string, fieldNameIPAddress: string, message: string = 'Not included in the cidr range') {
  return this.test({
    name: 'ip-is-over',
    // eslint-disable-next-line
    test: function (value: string) {
      const { path, createError, parent } = this;
      if (value !== '') {
        let ip;
        let ipOrigin;
        try {
          switch (parent[fieldNameIPType]) {
            case 'V4':
              ip = new Address4(value);
              ipOrigin = new Address4(parent[fieldNameIPAddress]);
              break;
            case 'V6':
              ip = new Address6(value);
              ipOrigin = new Address6(parent[fieldNameIPAddress]);
              break;
            default:
              break;
          }
        } catch (error) {
          return createError({ path, message: 'Error is not IPv4 or IPv6' });
        }
        try {
          if ((ipOrigin as any)?.bigInteger() > (ip as any)?.bigInteger()) {
            return createError({ path, message: `'${path}' IP must be over '${fieldNameIPAddress}'` });
          }
        } catch (error) {
          return createError({ path, message: 'Error on bigInteger check' });
        }
      }
      return true;
    },
  });
}

// eslint-disable-next-line no-unused-vars
function isAlphanumericHypenBracketSpace(this: any, message: string = 'Invalid string format') {
  return this.matches(
    /^[a-zA-Z0-9-_ [\]]+$/,
    {
      message: 'Accept only alphanumeric, [], space, - or _',
      excludeEmptyString: true,
    },
  );
}

// eslint-disable-next-line no-unused-vars
function isAlphanumericHypenBracketColonDot(this: any, message: string = 'Invalid string format') {
  return this.matches(
    /^[a-zA-Z0-9-_ [\]]+$/,
    {
      message: 'Accept only alphanumeric, -, _, [], : or .',
      excludeEmptyString: true,
    },
  );
}

// eslint-disable-next-line no-unused-vars
function isNumeric(this: any, message: string = 'Invalid number') {
  return this.matches(
    /^[0-9]+$/,
    {
      message: 'Must be a positive number',
      excludeEmptyString: true,
    },
  );
}

// eslint-disable-next-line no-unused-vars
function isInt(this: any, message: string = 'Invalid number') {
  return this.test({
    name: 'integer',
    // eslint-disable-next-line
    test: function (value: string) {
      const { path, createError } = this;
      const numberValue = +value;
      if (Number.isNaN(numberValue)) {
        return createError({ path, message: 'Must be a number' });
      }
      if (numberValue > 2147483647) {
        return createError({ path, message: `Must be inferior to ${2147483647}` });
      }
      return true;
    },
  });
}

// eslint-disable-next-line no-unused-vars
function isUInt(this: any, message: string = 'Invalid number') {
  return this.test({
    name: 'uinteger',
    // eslint-disable-next-line
    test: function (value: string) {
      const { path, createError } = this;
      const numberValue = +value;
      if (Number.isNaN(numberValue)) {
        return createError({ path, message: 'Must be a number' });
      }
      if (numberValue < 0) {
        return createError({ path, message: 'Must be superior or equal to 0' });
      }
      if (numberValue > 4294967295) {
        return createError({ path, message: `Must be inferior to ${4294967295}` });
      }
      return true;
    },
  });
}

// eslint-disable-next-line no-unused-vars
function passwordIdentical(this: any, otherPasswordInputFieldName: string, message: string = 'Password not identical') {
  return this.test({
    name: 'password-identical',
    // eslint-disable-next-line
    test: function (value: string) {
      const { path, createError, parent } = this;
      if (parent[otherPasswordInputFieldName] !== ''
      && value !== parent[otherPasswordInputFieldName]) {
        return createError({ path, message: 'Passwords are not identical' });
      }
      return true;
    },
  });
}

// eslint-disable-next-line no-unused-vars
function isJWT(this: any, message: string = 'Invalid JWT') {
  return this.test({
    name: 'jwt',
    // eslint-disable-next-line
    test: function (value: string) {
      const { path, createError } = this;
      if (!value) return true;
      if (value.split('.').length !== 3) {
        return createError({ path, message: 'Invalid token format' });
      }
      try {
        jwtDecode(value);
      } catch (error: any) {
        return createError({ path, message: 'Invalid token' });
      }
      return true;
    },
  });
}

yup.addMethod(yup.string, 'ipv4WithCIDR', ipv4WithCIDR);
yup.addMethod(yup.string, 'ipv4', ipv4);
yup.addMethod(yup.string, 'ipv6WithCIDR', ipv6WithCIDR);
yup.addMethod(yup.string, 'ipv6', ipv6);
yup.addMethod(yup.string, 'ipIsInSubnet', ipIsInSubnet);
yup.addMethod(yup.string, 'ipIsOver', ipIsOver);
yup.addMethod(yup.string, 'isAlphanumericHypenBracketSpace', isAlphanumericHypenBracketSpace);
yup.addMethod(yup.string, 'isAlphanumericHypenBracketColonDot', isAlphanumericHypenBracketColonDot);
yup.addMethod(yup.string, 'isNumeric', isNumeric);
yup.addMethod(yup.string, 'isInt', isInt);
yup.addMethod(yup.string, 'isUInt', isUInt);
yup.addMethod(yup.string, 'passwordIdentical', passwordIdentical);
yup.addMethod(yup.string, 'isJWT', isJWT);

export default yup;
